import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { guildIconCdnUrl } from "@cookies/shared/cdn";
import { mutate, useSnapshot } from "../../data/store";
import { applyMusicSetting, unlockAudioOnce } from "../../game/music";

export function UserMenu({ username, onSignOut }: { username: string; onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const el = wrapRef.current;
      if (el !== null && e.target instanceof Node && !el.contains(e.target)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("pointerdown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div className="bk-usermenu" ref={wrapRef}>
      <button type="button" className="bk-authbtn bk-userbtn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {username}
      </button>
      {open && (
        <div className="bk-usermenu-pop" role="menu">
          <button type="button" role="menuitem" className="bk-authbtn bk-signout" onClick={() => { setOpen(false); onSignOut(); }}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export function MuteButton(): ReactNode {
  const { state } = useSnapshot();
  const on = state.settings.music;
  return (
    <button
      type="button"
      className="bk-mute"
      aria-label={on ? "Mute music" : "Unmute music"}
      aria-pressed={on}
      title={on ? "Mute music" : "Unmute music"}
      onClick={() => {
        const next = !on;
        if (next) unlockAudioOnce();
        mutate((st) => ({ ...st, settings: { ...st.settings, music: next } }));
        applyMusicSetting(next);
      }}
    >
      {on ? "🔊" : "🔇"}
    </button>
  );
}

function guildIconUrl(guildId: string, icon: string | null | undefined): string {
  const v = guildIconCdnUrl(guildId, icon ?? null, 64);
  return v ?? "";
}

export function GuildBrandIcon({ guildId, icon, name }: { guildId: string; icon: string | null | undefined; name: string }) {
  const url = guildIconUrl(guildId, icon);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!url || failed || loaded) return;
    const t = window.setTimeout(() => setFailed(true), 1000);
    return () => window.clearTimeout(t);
  }, [url, failed, loaded]);
  if (url && !failed) {
    return <span className="bk-brand-ico" style={{ padding: 0, overflow: "hidden" }} aria-hidden="true"><img src={url} alt={name} width={44} height={44} crossOrigin="anonymous" style={{ width: "100%", height: "100%", borderRadius: 10, objectFit: "cover" }} onLoad={() => setLoaded(true)} onError={() => setFailed(true)} /></span>;
  }
  return <span className="bk-brand-ico" aria-hidden="true">{name.slice(0, 1) || "🏠"}</span>;
}
